import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { slugify } from '../utils/textUtils';
import {
    Box,
    Typography,
    CardMedia,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    Pagination,
    Chip,
    Tooltip,
    Stack,
    MenuItem,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { apiService } from '../services/api';

const Podcasts: React.FC = () => {
    const { t } = useTranslation('explorar');
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<string>('views');
    const [page, setPage] = useState(1);

    // Debounce search term to avoid excessive API calls
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300); // 300ms delay

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const {
        data: podcastsData,
        isLoading,
        error,
        isFetching,
    } = useQuery({
        queryKey: ['podcasts', page, debouncedSearchTerm, sortBy],
        queryFn: () => apiService.getPodcasts(page, 20, debouncedSearchTerm, undefined, sortBy),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
        placeholderData: (previousData) => previousData, // Keep previous data while fetching
    });

    // Fetch Q1 analytics data
    const {
        data: analyticsData,
        isLoading: analyticsLoading,
    } = useQuery({
        queryKey: ['podcast-analytics-q1'],
        queryFn: () => apiService.getPodcastAnalyticsQ1(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
    });

    const podcasts = podcastsData?.podcasts || [];
    const totalPages = podcastsData?.pagination?.pages || 1;
    const totalPodcasts = podcastsData?.pagination?.total || 0;

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setPage(1); // Reset to first page when searching
    }, []);

    const handleSortChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
        setSortBy(event.target.value as string);
        setPage(1); // Reset to first page when changing sort
    }, []);

    const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    }, []);

    const getFormattedViewCount = useCallback((podcastId: string) => {
        const views = analyticsData?.[podcastId]?.views || 0;
        return views.toLocaleString('es-ES');
    }, [analyticsData]);

    const isInitialLoading = isLoading && podcasts.length === 0;
    const isSearching = isFetching && podcasts.length > 0;

    return (
        <>
            {/* Search Bar and Sort */}
            <Box sx={{ mb: 4, display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
                <TextField
                    label={t('podcasts.search')}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                        endAdornment: isSearching ? (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{ minWidth: 200, flex: 1 }}
                />
                <TextField
                    select
                    label={t('podcasts.sortBy')}
                    value={sortBy}
                    onChange={handleSortChange}
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="title">{t('podcasts.title')}</MenuItem>
                    <MenuItem value="views">{t('podcasts.views')}</MenuItem>
                </TextField>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {t('podcasts.showing', { total: totalPodcasts.toLocaleString() })}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {t('podcasts.loadError')}
                </Alert>
            )}

            {/* Initial Loading */}
            {isInitialLoading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1,
                        minHeight: '50vh',
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {/* Podcasts Grid */}
            {!isInitialLoading && (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(4, 1fr)'
                    },
                    gap: 3,
                    mb: 4,
                    position: 'relative',
                }}>
                    {podcasts.map((podcast) => (
                        <Box
                            key={podcast._id}
                            onClick={() => navigate(`/explorar/podcasts/${podcast.slug || slugify(podcast.title)}`)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                position: 'relative',
                                transition: 'all 0.2s ease-in-out',
                                cursor: 'pointer',
                                height: '100%',
                                backgroundColor: 'white',
                            }}
                        >
                            {/* Network Badge - Mock for now */}
                            {podcast.category && (
                                <Tooltip title={t('podcasts.category', { category: podcast.category })} arrow>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            right: 16,
                                            zIndex: 1,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                transform: 'scale(1.1)',
                                            },
                                        }}
                                    >
                                        <Chip
                                            label={podcast.category}
                                            size="small"
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                            )}

                            {/* Top - Image */}
                            <Box sx={{ position: 'relative' }}>
                                <CardMedia
                                    component="img"
                                    height="200"
                                    image={podcast.imageUrl || podcast.youtubeData?.snippet?.thumbnails?.high?.url}
                                    alt={podcast.title}
                                    sx={{
                                        objectFit: 'cover',
                                        width: '100%',
                                        height: '200px',
                                        borderRadius: 2,
                                    }}
                                />
                            </Box>

                            {/* Bottom - Content */}
                            <Box
                                sx={{
                                    pt: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            textAlign: 'left',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '100%',
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            color: 'primary.main',
                                            mb: 1,
                                        }}
                                    >
                                        {podcast.title}
                                    </Typography>
                                    {podcast.description && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                lineHeight: 1.4,
                                                mb: 2,
                                            }}
                                        >
                                            {podcast.description}
                                        </Typography>
                                    )}
                                </Box>

                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1}
                                    sx={{ width: '100%' }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Tooltip
                                            title={t('podcasts.viewCount', {
                                                total: getFormattedViewCount(podcast._id),
                                            })}
                                            arrow
                                        >
                                            <Typography
                                                variant="body2"
                                                color="primary"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                <Visibility sx={{ fontSize: 16 }} />
                                                {getFormattedViewCount(podcast._id)}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                </Stack>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                    />
                </Box>
            )}
        </>
    );
};

export default Podcasts; 